// Creative Content v1 → Creative Document v2 (CREATIVE-MODEL-V1.md §14.1).
//
// v1 (7.4) is only an envelope without a catalog. The wizard creates it EMPTY;
// that empty envelope becomes a v2 skeleton: one format variant, one empty
// scene, fallback pending (it is required to leave DRAFT, not to save). A v1
// envelope WITH content (only possible through the development JSON tool) is
// not migrated: there is no semantics to preserve, and guessing is forbidden.
//
// Pure and deterministic: same input ⇒ same document. Applied to DRAFT only
// (callers enforce ADR-0005).

import { CREATIVE_PRODUCTS, type CreativeProduct } from "@interactads/domain";
import { FORMAT_KEY_PATTERN, type CreativeDocument } from "./schema/document.js";
import { isPlainObject } from "./schema/dsl.js";
import { validateCreativeDocument } from "./validate/index.js";
import type { ValidationIssue } from "./issues.js";

export interface SkeletonInit {
  readonly intendedProduct: CreativeProduct;
  /** BCP 47, e.g. "es". */
  readonly locale: string;
  readonly title?: string;
  /** Accessible name of the ad (required by the model; editable later). */
  readonly accessibilityLabel: string;
  /** First format variant, e.g. { key: "300x250", width: 300, height: 250 }. */
  readonly format: { readonly key: string; readonly width: number; readonly height: number };
}

export type MigrationResult =
  | { readonly ok: true; readonly document: CreativeDocument }
  | {
      readonly ok: false;
      readonly code: "V1_NOT_EMPTY" | "INVALID_INIT";
      readonly issues?: readonly ValidationIssue[];
    };

/** Layout id derived from a format key (ids are /^[A-Za-z0-9_-]{1,64}$/). */
export function layoutIdForFormat(formatKey: string): string {
  return `l${formatKey.replace(/[^A-Za-z0-9_-]/g, "_")}`.slice(0, 64);
}

/** Whether a Creative Content v1 value is the empty envelope the wizard creates. */
export function isEmptyCreativeContentV1(content: unknown): boolean {
  if (!isPlainObject(content)) return false;
  const keys = Object.keys(content);
  if (keys.some((k) => k !== "components" && k !== "rules" && k !== "metadata")) return false;
  const empty = (v: unknown) => v === undefined || (Array.isArray(v) && v.length === 0);
  const emptyObject = (v: unknown) => v === undefined || (isPlainObject(v) && Object.keys(v).length === 0);
  return (
    Array.isArray(content.components) &&
    empty(content.components) &&
    empty(content.rules) &&
    emptyObject(content.metadata)
  );
}

/** A new, empty v2 document (valid to save; not yet ready to leave DRAFT). */
export function createSkeletonDocument(init: SkeletonInit): MigrationResult {
  if (!CREATIVE_PRODUCTS.includes(init.intendedProduct) || !FORMAT_KEY_PATTERN.test(init.format.key))
    return { ok: false, code: "INVALID_INIT" };
  const candidate = {
    kind: "interactads.creative",
    schemaVersion: 2,
    meta: {
      intendedProduct: init.intendedProduct,
      locale: init.locale,
      ...(init.title !== undefined ? { title: init.title } : {}),
      accessibility: { label: init.accessibilityLabel },
    },
    layouts: [
      {
        id: layoutIdForFormat(init.format.key),
        formatKeys: [init.format.key],
        sizing: { mode: "fixed", width: init.format.width, height: init.format.height },
      },
    ],
    scenes: [
      {
        id: "main",
        name: "Principal",
        role: "content",
        duration: null,
        background: { kind: "solid", color: "#FFFFFF" },
        children: [],
      },
    ],
    initialSceneId: "main",
    elements: [],
    assets: [],
    links: [],
  };
  const result = validateCreativeDocument(candidate);
  if (!result.canSave || !result.document) return { ok: false, code: "INVALID_INIT", issues: result.issues };
  return { ok: true, document: result.document };
}

/** v1 → v2 for the empty v1 envelope only. */
export function migrateCreativeContentV1ToV2(content: unknown, init: SkeletonInit): MigrationResult {
  if (!isEmptyCreativeContentV1(content)) return { ok: false, code: "V1_NOT_EMPTY" };
  return createSkeletonDocument(init);
}
